/**
 * 🔎 useWorkFilters — Filtros e ordenação para Minha Mesa
 *
 * Persiste em localStorage. Debounce de 200ms.
 * Pipeline: items → período → tipo → status → ordenação
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UnifiedWorkItem, WorkItemSource, WorkItemStatus } from '../types/workItem';

// ─── Types ──────────────────────────────────────────────────

export type PeriodFilter = 'all' | 'today' | 'yesterday' | '7d' | '30d' | 'this_month' | 'last_month' | 'this_year' | 'custom';
export type SortMode = 'fila' | 'fifo' | 'sla_asc' | 'lead_desc' | 'idle_desc' | 'priority';

export interface WorkFilters {
  period: PeriodFilter;
  customFrom: string | null;
  customTo: string | null;
  sourceTypes: WorkItemSource[];
  statuses: WorkItemStatus[];
  sort: SortMode;
}

export const DEFAULT_FILTERS: WorkFilters = {
  period: 'all',
  customFrom: null,
  customTo: null,
  sourceTypes: [],
  statuses: [],
  sort: 'fila',
};

const STORAGE_KEY = 'workflow.filters.minhaMesa';
const STORAGE_VERSION_KEY = 'workflow.filters.minhaMesa.v';
const STORAGE_VERSION = '2';

// ─── Helpers ────────────────────────────────────────────────

function loadPersistedFilters(): WorkFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<WorkFilters>;
    const merged: WorkFilters = { ...DEFAULT_FILTERS, ...parsed };
    const version = localStorage.getItem(STORAGE_VERSION_KEY);
    if (version !== STORAGE_VERSION) {
      if (!parsed.sort || parsed.sort === 'fifo') merged.sort = 'fila';
      persistFilters(merged);
      localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);
    }
    return merged;
  } catch {
    return DEFAULT_FILTERS;
  }
}

function persistFilters(f: WorkFilters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch { /* noop */ }
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function isInPeriod(dateStr: string | null | undefined, filter: WorkFilters): boolean {
  if (!dateStr) return true; // no date = don't filter out
  const d = new Date(dateStr);
  const now = new Date();
  switch (filter.period) {
    case 'all': return true;
    case 'today': return d >= startOfDay(now);
    case 'yesterday': {
      const yd = startOfDay(now); yd.setDate(yd.getDate() - 1);
      return d >= yd && d < startOfDay(now);
    }
    case '7d': { const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 7); return d >= cutoff; }
    case '30d': { const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 30); return d >= cutoff; }
    case 'this_month': { const first = new Date(now.getFullYear(), now.getMonth(), 1); return d >= first; }
    case 'last_month': { const first = new Date(now.getFullYear(), now.getMonth() - 1, 1); const end = new Date(now.getFullYear(), now.getMonth(), 1); return d >= first && d < end; }
    case 'this_year': { const jan1 = new Date(now.getFullYear(), 0, 1); return d >= jan1; }
    case 'custom': {
      if (filter.customFrom && d < new Date(filter.customFrom)) return false;
      if (filter.customTo && d > new Date(filter.customTo)) return false;
      return true;
    }
    default: return true;
  }
}

function getIdleMs(item: UnifiedWorkItem): number {
  const lastActivity = item.started_at || item.created_at;
  return Date.now() - new Date(lastActivity).getTime();
}

function getLeadMs(item: UnifiedWorkItem): number {
  const end = item.completed_at ? new Date(item.completed_at).getTime() : Date.now();
  return end - new Date(item.created_at).getTime();
}

function getSlaMs(item: UnifiedWorkItem): number {
  const deadline = item.source_context?.sla_deadline || item.due_date;
  if (!deadline) return Infinity;
  return new Date(deadline).getTime() - Date.now();
}

// ─── Hook ───────────────────────────────────────────────────

export function useWorkFilters(items: UnifiedWorkItem[]) {
  const [filters, setFiltersRaw] = useState<WorkFilters>(loadPersistedFilters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedFilters, setDebouncedFilters] = useState<WorkFilters>(filters);

  const setFilters = useCallback((updater: WorkFilters | ((prev: WorkFilters) => WorkFilters)) => {
    setFiltersRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      persistFilters(next);
      return next;
    });
  }, []);

  // Debounce 200ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedFilters(filters), 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filters]);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.period !== 'all' ||
      filters.sourceTypes.length > 0 ||
      filters.statuses.length > 0 ||
      filters.sort !== 'fila'
    );
  }, [filters]);

  // Pipeline: período → tipo → status → ordenação
  const filteredItems = useMemo(() => {
    const f = debouncedFilters;
    let result = items;

    // 1. Period filter
    if (f.period !== 'all') {
      result = result.filter((item) =>
        isInPeriod(item.created_at, f) ||
        isInPeriod(item.due_date, f) ||
        isInPeriod(item.source_context?.sla_deadline, f)
      );
    }

    // 2. Source type filter (multi-select, empty = all)
    if (f.sourceTypes.length > 0) {
      result = result.filter((item) => f.sourceTypes.includes(item.source));
    }

    // 3. Status filter (multi-select, empty = all)
    if (f.statuses.length > 0) {
      result = result.filter((item) => f.statuses.includes(item.status));
    }

    // 4. Sort
    result = [...result].sort((a, b) => {
      switch (f.sort) {
        case 'fila': {
          if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
          const sla = getSlaMs(a) - getSlaMs(b);
          if (sla !== 0) return sla;
          const idle = getIdleMs(b) - getIdleMs(a);
          if (idle !== 0) return idle;
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }
        case 'sla_asc': return getSlaMs(a) - getSlaMs(b);
        case 'fifo': return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'lead_desc': return getLeadMs(b) - getLeadMs(a);
        case 'idle_desc': return getIdleMs(b) - getIdleMs(a);
        case 'priority': {
          const pA = a.priority === 'high' ? 0 : a.priority === 'medium' ? 1 : 2;
          const pB = b.priority === 'high' ? 0 : b.priority === 'medium' ? 1 : 2;
          return pA - pB || getSlaMs(a) - getSlaMs(b);
        }
        default: return 0;
      }
    });

    return result;
  }, [items, debouncedFilters]);

  return { filters, setFilters, resetFilters, hasActiveFilters, filteredItems };
}
